import { useSyncExternalStore } from 'react';

/**
 * Monit „Zainstaluj aplikację” (PWA zamiast APK — DECISIONS 2026-10-04). Chrome wysyła `beforeinstallprompt` raz,
 * zaraz po wczytaniu strony (gdy aplikacja spełnia wymagania: manifest, service worker, HTTPS): zdarzenie zatrzymujemy
 * i pokazujemy monit dopiero na prośbę osoby (Ustawienia → Powiadomienia). iPhone nie ma tego zdarzenia — instrukcja
 * „Udostępnij → Do ekranu początkowego”. Moduł nasłuchuje od importu (ładuje go szkielet aplikacji).
 */

interface BeforeInstallPromptEventLike extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

/** installed — działa jako aplikacja (albo właśnie zainstalowana), available — można pokazać monit przeglądarki,
 * ios — Safari: ręcznie przez „Udostępnij”, unavailable — przeglądarka nie proponuje instalacji (dev, HTTP, Firefox). */
export type InstallState = 'installed' | 'available' | 'ios' | 'unavailable';

let deferred: BeforeInstallPromptEventLike | null = null;
let justInstalled = false;
let attached = false;
let version = 0;
const listeners = new Set<() => void>();

function notify(): void {
  version++;
  for (const l of listeners) l();
}

export function captureInstallPrompt(target: Window | undefined = typeof window === 'undefined' ? undefined : window) {
  if (!target || attached) return;
  attached = true;
  target.addEventListener('beforeinstallprompt', (event) => {
    event.preventDefault();
    deferred = event as BeforeInstallPromptEventLike;
    notify();
  });
  target.addEventListener('appinstalled', () => {
    deferred = null;
    justInstalled = true;
    notify();
  });
}

captureInstallPrompt();

/** Aplikacja uruchomiona z ekranu głównego (tryb standalone). */
export function isStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  const ios = (window.navigator as Navigator & { standalone?: boolean }).standalone === true;
  return ios || (typeof window.matchMedia === 'function' && window.matchMedia('(display-mode: standalone)').matches);
}

function isIos(): boolean {
  if (typeof navigator === 'undefined') return false;
  return /iPhone|iPad|iPod/.test(navigator.userAgent);
}

export function installState(): InstallState {
  if (justInstalled || isStandalone()) return 'installed';
  if (deferred) return 'available';
  if (isIos()) return 'ios';
  return 'unavailable';
}

/** Pokazuje monit przeglądarki (raz na zdarzenie). */
export async function promptInstall(): Promise<'accepted' | 'dismissed' | 'unavailable'> {
  const event = deferred;
  if (!event) return 'unavailable';
  deferred = null;
  await event.prompt();
  const choice = await event.userChoice;
  if (choice.outcome === 'accepted') justInstalled = true;
  notify();
  return choice.outcome;
}

/** testy */
export function resetInstallPrompt(): void {
  deferred = null;
  justInstalled = false;
  notify();
}

export function useInstallPrompt(): {
  state: InstallState;
  install: () => Promise<'accepted' | 'dismissed' | 'unavailable'>;
} {
  useSyncExternalStore(
    (l) => {
      listeners.add(l);
      return () => listeners.delete(l);
    },
    () => version,
    () => version,
  );
  return { state: installState(), install: promptInstall };
}
